// A document's attachments (the Mac's InspectorViews `Attachments` section and `PhotoStrip`): the pictures as a strip
// of thumbnails, the other files as rows with Open. The bytes are the relay's `attachment.<hmac>` snapshots this
// browser has cached, opened with the project key on demand (`LiveProject.attachment`) and shown through object URLs;
// a file that hasn't reached this device yet says so. Files are added on a Mac or iPad, or by a Scrivener import.
import type { AttachmentRef } from '@writing-suite/core';
import { useEffect, useState } from 'react';
import type { LiveProject } from '../store/project.ts';

/**
 * Types the browser shows without running anything, so a file of one may open in a tab. The stored `mimeType` is the
 * document's word, which anyone who can write the project (a co-writer, a Scrivener project being imported) chooses:
 * an object URL has this page's origin, so HTML, SVG or XML opened from one would be a page of the app's own.
 */
const VIEWABLE = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/heic', 'image/avif', 'application/pdf']);

/** The type an attachment's object URL is made with: its own when viewable, else bytes to save. */
export const blobType = (mimeType: string) => {
  const type = mimeType.toLowerCase().split(';')[0].trim();
  return VIEWABLE.has(type) ? type : 'application/octet-stream';
};

export const isPicture = (a: AttachmentRef) => blobType(a.mimeType).startsWith('image/');
const isViewable = (a: AttachmentRef) => blobType(a.mimeType) !== 'application/octet-stream';

/** An object URL for an attachment's bytes, or null while loading or when the file isn't here; revoked on unmount. */
export function useAttachmentURL(live: LiveProject | null, ref: AttachmentRef | null): { url: string | null; missing: boolean } {
  const [state, setState] = useState<{ url: string | null; missing: boolean }>({ url: null, missing: false });
  const sha = ref?.sha256 ?? null;
  const type = blobType(ref?.mimeType ?? '');
  useEffect(() => {
    if (!live || !sha) { setState({ url: null, missing: false }); return; }
    let url: string | null = null;
    let cancelled = false;
    void live.attachment(sha).then((bytes) => {
      if (cancelled) return;
      if (!bytes) { setState({ url: null, missing: true }); return; }
      url = URL.createObjectURL(new Blob([bytes as BlobPart], { type }));
      setState({ url, missing: false });
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [live, sha, type]);
  return state;
}

function Thumb({ live, ref }: { live: LiveProject | null; ref: AttachmentRef }) {
  const { url, missing } = useAttachmentURL(live, ref);
  if (url) return <img src={url} alt={ref.filename} title={ref.filename} />;
  return <span className="attachment-thumb-pending" title={missing ? `${ref.filename} isn’t on this device yet` : ref.filename} aria-label={ref.filename} />;
}

function FileRow({ live, ref }: { live: LiveProject | null; ref: AttachmentRef }) {
  // Opened as the section shows (the bytes are in this browser's cache, not on the network), so Open and Save are plain
  // links: a link's own click opens the new tab, where opening one after an await would be blocked as a popup.
  const { url, missing } = useAttachmentURL(live, ref);
  return (
    <li className="attachment-row" data-attachment={ref.sha256}>
      <span className="attachment-name">{ref.filename || 'Attachment'}</span>
      <span className="attachment-type">{ref.mimeType}</span>
      {url ? (
        <>
          {isViewable(ref) ? <a href={url} target="_blank" rel="noopener noreferrer">Open</a> : null}
          <a href={url} download={ref.filename || 'attachment'}>Save</a>
        </>
      ) : missing ? (
        <span className="attachment-note">Not on this device yet</span>
      ) : (
        <span className="attachment-note">Loading…</span>
      )}
    </li>
  );
}

export function AttachmentsSection({ live, attachments }: { live: LiveProject | null; attachments: AttachmentRef[] }) {
  if (!attachments.length) return null;
  const pictures = attachments.filter(isPicture);
  const files = attachments.filter((a) => !isPicture(a));
  return (
    <section className="inspector-attachments" aria-labelledby="inspector-attachments-title" data-attachments>
      <h3 id="inspector-attachments-title" className="eyebrow">Attachments</h3>
      {pictures.length ? (
        <div className="attachment-thumbs">
          {pictures.map((a) => <Thumb key={a.sha256} live={live} ref={a} />)}
        </div>
      ) : null}
      {files.length ? (
        <ul>
          {files.map((a) => <FileRow key={a.sha256} live={live} ref={a} />)}
        </ul>
      ) : null}
    </section>
  );
}
